import React, { useContext, useMemo } from 'react';
import { SimcardsOnlineGraphTypes } from '@utils/alarmsConstants';
import SessionContext from '@providers/Session/SessionContext';
import useSwr from '@hooks/useSwr';
import { ProductField } from './ProductField/ProductField';
import { TechnologyField } from './TechnologyField/TechnologyField';
import { OperatorField } from './OperatorField/OperatorField';
import { StateField } from './StateField/StateField';
import { MacroregionField } from './MacroregionField/MacroregionField';

export interface ListResponseSimcardsOnline {
  success: boolean;
  data?: string[];
}

interface SimcardsOnlineFieldsProps {
  graphType: string;
  readOnlyFields?: string[];
}

const processData = (data: ListResponseSimcardsOnline | undefined) => {
  if (!data) return [];

  const items = Array.isArray(data) ? data : data.data || [];

  return items.map(item => ({
    label: item,
    value: item,
  }));
};

export default function SimcardsOnlineFields({
  graphType,
  readOnlyFields = [],
}: SimcardsOnlineFieldsProps) {
  const { customer } = useContext(SessionContext);
  const customerId = customer?.customerLogged?.id;

  const { data: technologiesData } = useSwr<ListResponseSimcardsOnline>(
    customerId
      ? '/service-proxy/dashboard/simcard_online/alarme/kapacitor/alerta_task/listar_tecnologia'
      : null,
    {
      headers: {
        customerId,
      },
    },
  );

  const { data: statesData } = useSwr<ListResponseSimcardsOnline>(
    customerId
      ? '/service-proxy/dashboard/simcard_online/alarme/kapacitor/alerta_task/listar_estado'
      : null,
    {
      headers: {
        customerId,
      },
    },
  );

  const { data: productsData } = useSwr<ListResponseSimcardsOnline>(
    customerId
      ? '/service-proxy/dashboard/simcard_online/alarme/kapacitor/alerta_task/listar_produto'
      : null,
    {
      headers: {
        customerId,
      },
    },
  );

  const { data: operatorsData } = useSwr<ListResponseSimcardsOnline>(
    customerId
      ? '/service-proxy/dashboard/simcard_online/alarme/kapacitor/alerta_task/listar_operadora'
      : null,
    {
      headers: {
        customerId,
      },
    },
  );

  const { data: macroRegionsData } = useSwr<ListResponseSimcardsOnline>(
    customerId
      ? '/service-proxy/dashboard/simcard_online/alarme/kapacitor/alerta_task/listar_macro_regiao'
      : null,
    {
      headers: {
        customerId,
      },
    },
  );

  const technologiesOptions = useMemo(
    () => processData(technologiesData),
    [technologiesData],
  );
  const statesOptions = useMemo(() => processData(statesData), [statesData]);
  const productsOptions = useMemo(
    () => processData(productsData),
    [productsData],
  );
  const operatorsOptions = useMemo(
    () => processData(operatorsData),
    [operatorsData],
  );
  const macroRegionsOptions = useMemo(
    () => processData(macroRegionsData),
    [macroRegionsData],
  );

  switch (graphType) {
    case SimcardsOnlineGraphTypes.PRODUCT_GRAPH:
      return (
        <ProductField
          options={productsOptions}
          loading={!productsData}
          disabled={readOnlyFields.includes('produtos')}
        />
      );

    case SimcardsOnlineGraphTypes.TECHNOLOGY_GRAPH:
      return (
        <TechnologyField
          options={technologiesOptions}
          loading={!technologiesData}
          disabled={readOnlyFields.includes('tecnologias')}
        />
      );

    case SimcardsOnlineGraphTypes.OPERATOR_GRAPH:
      return (
        <OperatorField
          options={operatorsOptions}
          loading={!operatorsData}
          disabled={readOnlyFields.includes('operadoras')}
        />
      );

    case SimcardsOnlineGraphTypes.STATE_GRAPH:
      return (
        <StateField
          options={statesOptions}
          loading={!statesData}
          disabled={readOnlyFields.includes('estados')}
        />
      );

    case SimcardsOnlineGraphTypes.MACROREGIAO_GRAPH:
      return (
        <MacroregionField
          options={macroRegionsOptions}
          loading={!macroRegionsData}
          disabled={readOnlyFields.includes('macroregioes')}
        />
      );

    default:
      return null;
  }
}
